/**
 * clientTools.js — all file processing runs in the user's browser.
 * No uploads, no server cost, no privacy risk. Scales infinitely for free.
 */
import { PDFDocument } from "pdf-lib";
import QRCode from "qrcode";

/* ---------------- PDF ---------------- */

export async function pdfMerge(files) {
  if (files.length < 2) throw new Error("Upload at least 2 PDFs");
  const out = await PDFDocument.create();
  for (const f of files) {
    const bytes = await f.arrayBuffer();
    let src;
    try {
      src = await PDFDocument.load(bytes, { ignoreEncryption: true });
    } catch {
      throw new Error(`Invalid PDF: ${f.name}`);
    }
    const pages = await out.copyPages(src, src.getPageIndices());
    pages.forEach((p) => out.addPage(p));
  }
  const merged = await out.save();
  return { blob: new Blob([merged], { type: "application/pdf" }), name: "merged.pdf" };
}

function parseRanges(spec, total) {
  const pages = new Set();
  for (let part of spec.split(",")) {
    part = part.trim();
    if (!part) continue;
    if (part.includes("-")) {
      const [a, b] = part.split("-", 2).map((n) => parseInt(n, 10));
      for (let i = a; i <= b; i++) pages.add(i);
    } else {
      pages.add(parseInt(part, 10));
    }
  }
  return [...pages].filter((p) => p >= 1 && p <= total).sort((x, y) => x - y);
}

export async function pdfSplit(file, ranges) {
  const bytes = await file.arrayBuffer();
  let src;
  try {
    src = await PDFDocument.load(bytes, { ignoreEncryption: true });
  } catch {
    throw new Error("Invalid PDF");
  }
  const total = src.getPageCount();
  const selected = ranges.trim()
    ? parseRanges(ranges, total)
    : Array.from({ length: total }, (_, i) => i + 1);
  if (!selected.length) throw new Error("No valid pages selected");
  const out = await PDFDocument.create();
  const pages = await out.copyPages(src, selected.map((p) => p - 1));
  pages.forEach((p) => out.addPage(p));
  const result = await out.save();
  return { blob: new Blob([result], { type: "application/pdf" }), name: "split.pdf" };
}

export async function pdfCompress(file) {
  const bytes = await file.arrayBuffer();
  let src;
  try {
    src = await PDFDocument.load(bytes, { ignoreEncryption: true });
  } catch {
    throw new Error("Invalid PDF");
  }
  // Re-serialising with object streams gives a lossless size reduction,
  // same class of result as the old pypdf compress_content_streams.
  const result = await src.save({ useObjectStreams: true });
  return { blob: new Blob([result], { type: "application/pdf" }), name: "compressed.pdf" };
}

export async function imagesToPdf(files) {
  if (!files.length) throw new Error("Upload at least 1 image");
  const out = await PDFDocument.create();
  for (const f of files) {
    // Normalise everything to JPEG via canvas so HEIC/WebP/PNG all work.
    const dataUrl = await fileToJpegDataUrl(f, 0.92);
    const jpgBytes = dataUrlToBytes(dataUrl);
    const img = await out.embedJpg(jpgBytes);
    const page = out.addPage([img.width, img.height]);
    page.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height });
  }
  const result = await out.save();
  return { blob: new Blob([result], { type: "application/pdf" }), name: "images.pdf" };
}

/* ---------------- Images (Canvas API) ---------------- */

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Invalid or unsupported image")); };
    img.src = url;
  });
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Conversion failed"))),
      mime,
      quality
    );
  });
}

async function fileToJpegDataUrl(file, quality) {
  const img = await loadImage(file);
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff"; // flatten transparency
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);
  return canvas.toDataURL("image/jpeg", quality);
}

function dataUrlToBytes(dataUrl) {
  const b64 = dataUrl.split(",")[1];
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

const MIME = { png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp" };

export async function imageConvert(file, targetFormat) {
  const mime = MIME[targetFormat];
  if (!mime) throw new Error("Unsupported target format");
  const img = await loadImage(file);
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (mime === "image/jpeg") { ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, canvas.width, canvas.height); }
  ctx.drawImage(img, 0, 0);
  const blob = await canvasToBlob(canvas, mime, 0.92);
  const base = (file.name || "image").replace(/\.[^.]+$/, "");
  return { blob, name: `${base}.${targetFormat === "jpeg" ? "jpg" : targetFormat}` };
}

export async function imageCompress(file, quality) {
  const img = await loadImage(file);
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  // Compress to WebP when the browser supports it (smaller), else JPEG.
  const mime = "image/webp";
  ctx.drawImage(img, 0, 0);
  let blob = await canvasToBlob(canvas, mime, quality / 100);
  let ext = "webp";
  if (blob.type !== "image/webp") { // Safari fallback
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, canvas.width, canvas.height); ctx.drawImage(img, 0, 0);
    blob = await canvasToBlob(canvas, "image/jpeg", quality / 100);
    ext = "jpg";
  }
  const base = (file.name || "image").replace(/\.[^.]+$/, "");
  return { blob, name: `${base}-compressed.${ext}` };
}

export async function imageResize(file, width, height, keepAspect) {
  const img = await loadImage(file);
  let w = Number(width) || 0;
  let h = Number(height) || 0;
  if (!w && !h) throw new Error("Set a width or height");
  if (keepAspect) {
    const ratio = img.naturalWidth / img.naturalHeight;
    if (w && !h) h = Math.round(w / ratio);
    else if (h && !w) w = Math.round(h * ratio);
    else h = Math.round(w / ratio);
  } else {
    if (!w) w = img.naturalWidth;
    if (!h) h = img.naturalHeight;
  }
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  const isPng = (file.type || "").includes("png");
  const blob = await canvasToBlob(canvas, isPng ? "image/png" : "image/jpeg", 0.92);
  const base = (file.name || "image").replace(/\.[^.]+$/, "");
  return { blob, name: `${base}-${w}x${h}.${isPng ? "png" : "jpg"}` };
}

/* ---------------- QR ---------------- */

export async function qrGenerate(text, size) {
  // size slider 4-20 maps to 200-1000px
  const px = Math.max(200, Math.min(1000, size * 50));
  return QRCode.toDataURL(text, {
    width: px,
    margin: 2,
    errorCorrectionLevel: "M",
    color: { dark: "#000000", light: "#ffffff" },
  });
}

/* ---------------- Local usage counter (replaces Mongo stats) ---------------- */

// No seeded numbers: counts reflect only this browser's own usage.
const BASELINE = { tools_run: 0, files_processed: 0 };
const KEY = "toolhaus-usage";

export function bumpUsage(field, by = 1) {
  try {
    const cur = JSON.parse(localStorage.getItem(KEY) || "{}");
    cur[field] = (cur[field] || 0) + by;
    localStorage.setItem(KEY, JSON.stringify(cur));
  } catch {}
}

export function getStats() {
  let local = {};
  try { local = JSON.parse(localStorage.getItem(KEY) || "{}"); } catch {}
  return {
    tools_run: BASELINE.tools_run + (local.tools_run || 0),
    files_processed: BASELINE.files_processed + (local.files_processed || 0),
    tools_available: 24,
  };
}
